import { Component, lazy, ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, matchRoutes, unstable_HistoryRouter as HistoryRouter, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { ClientTranslations } from '@/app/i18n/ClientTranslations';
import { isPublicRoute } from '@/features/auth';
import { languageState } from '@/recoil/atoms/languageState';
import { applicationBrowserHistory } from '@/shared/navigation/browserHistory';
import { ClientRouterProvider, useRouter } from '@/shared/navigation/clientRouter';
import { applyPreferredLocale } from '@/shared/navigation/localePath';
import { isReloadingForNewBuild } from '@/app/runtime/staleBuildReload';
import type { PageComponent, PageModule, RouteDefinition } from '@/shared/navigation/types';
import { applicationRoutes, errorPage, notFoundPage, supportedLocales } from './router/routeManifest';

const loadedRoutes = new Map<string, PageModule>();
const PageInitialize = lazy(() => import('@/components/Page/PageInitializeComponent'));

function RouteLoading() {
	const { t } = useTranslation('common');
	return <div aria-label={t('navigation.loading_page')} role="status" />;
}

function RouteLoadingPage() {
	return <RouteLoading />;
}

function LoadedPage({ loadingLayout, pageModule }: { loadingLayout?: PageComponent['getLayout']; pageModule?: PageModule }) {
	const router = useRouter();
	const Page = useMemo<PageComponent>(() => {
		if (pageModule) return pageModule.default;

		const LoadingPage = (() => <RouteLoadingPage />) as PageComponent;
		LoadingPage.getLayout = loadingLayout;
		return LoadingPage;
	}, [loadingLayout, pageModule]);

	return (
		<ClientTranslations locale={router.locale}>
			{pageModule?.default.bypassPageInitialization ? (
				<Page key={router.pageResetKey} />
			) : (
				<PageInitialize Component={Page} pageProps={{}} router={router} />
			)}
		</ClientTranslations>
	);
}

type RouteAccess = 'error' | 'private' | 'public';

function RouteModule({
	access,
	params,
	route
}: {
	access: RouteAccess;
	params?: Readonly<Record<string, string | undefined>>;
	route: RouteDefinition;
}) {
	const [loaded, setLoaded] = useState<{ access: RouteAccess; file: string; pageModule: PageModule } | undefined>(() => {
		const pageModule = loadedRoutes.get(route.file);
		return pageModule ? { access, file: route.file, pageModule } : undefined;
	});
	const [error, setError] = useState<Error>();

	useEffect(() => {
		let active = true;
		setError(undefined);
		const cached = loadedRoutes.get(route.file);
		if (cached) {
			setLoaded({ access, file: route.file, pageModule: cached });
			return () => {
				active = false;
			};
		}

		route
			.importPage()
			.then((pageModule) => {
				loadedRoutes.set(route.file, pageModule);
				if (active) setLoaded({ access, file: route.file, pageModule });
			})
			.catch((reason: unknown) => {
				if (active) setError(reason instanceof Error ? reason : new Error(String(reason)));
			});

		return () => {
			active = false;
		};
	}, [access, route]);

	if (error) throw error;
	// Read the cache during render so a known destination never takes a one-render detour
	// through the loading fallback while the effect mirrors it into state. For a first-time
	// same-access destination, keep the initialized application composition mounted and
	// replace only the route component with its loading surface. This preserves bootstrap,
	// theme, messages and shell state without rendering the previous page against the new
	// route context (which can suspend and deadlock the transition).
	const currentPage = loaded?.access === access && loaded.file === route.file ? loaded.pageModule : loadedRoutes.get(route.file);
	const keepInitializedComposition = !currentPage && loaded?.access === access && !loaded.pageModule.default.bypassPageInitialization;

	return (
		<ClientRouterProvider params={params} route={route} routes={applicationRoutes}>
			<Suspense fallback={<RouteLoading />}>
				{currentPage || keepInitializedComposition ? (
					<LoadedPage loadingLayout={loaded?.pageModule.default.getLayout} pageModule={currentPage} />
				) : (
					<RouteLoading />
				)}
			</Suspense>
		</ClientRouterProvider>
	);
}

interface RouteErrorBoundaryState {
	error?: Error;
}

class RouteErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, RouteErrorBoundaryState> {
	state: RouteErrorBoundaryState = {};

	static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
		return { error };
	}

	componentDidUpdate(previous: Readonly<{ children: ReactNode; resetKey: string }>) {
		if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: undefined });
	}

	render() {
		if (!this.state.error || !errorPage) return this.props.children;
		// A chunk a deploy replaced: the tab is already reloading onto the new build,
		// and the error page would only flash up for the moment before it lands.
		if (isReloadingForNewBuild()) return <RouteLoading />;
		const errorRoute: RouteDefinition = {
			file: '../../routes/500.tsx',
			importPage: errorPage,
			routePattern: '/500',
			reactPath: '/500'
		};
		return <RouteModule access="error" route={errorRoute} />;
	}
}

function localeNeutralPath(pathname: string): string {
	const [, firstSegment, ...rest] = pathname.split('/');
	if ((supportedLocales as readonly string[]).includes(firstSegment)) {
		return `/${rest.join('/')}` || '/';
	}
	return pathname;
}

function ApplicationRoutes() {
	const location = useLocation();
	const routePathname = localeNeutralPath(location.pathname);
	const fallbackRoute: RouteDefinition | undefined = notFoundPage
		? {
				file: '../../routes/404.tsx',
				importPage: notFoundPage,
				routePattern: '/404',
				reactPath: '*'
			}
		: undefined;
	const match = useMemo(
		() =>
			matchRoutes(
				applicationRoutes.map((route) => ({ handle: route, path: route.reactPath })),
				routePathname
			)?.at(-1),
		[routePathname]
	);
	const route = match?.route.handle ?? fallbackRoute;
	const access: RouteAccess = isPublicRoute(location.pathname, supportedLocales) ? 'public' : 'private';

	return (
		<RouteErrorBoundary resetKey={routePathname}>
			{route ? <RouteModule access={access} params={match?.params} route={route} /> : null}
		</RouteErrorBoundary>
	);
}

function PreferredLocaleRoutes() {
	const location = useLocation();
	const preferredLocale = useRecoilValue(languageState);
	const preferredPathname = applyPreferredLocale(location.pathname, preferredLocale);

	if (preferredPathname !== location.pathname) {
		return <Navigate replace to={`${preferredPathname}${location.search}${location.hash}`} />;
	}

	return <ApplicationRoutes />;
}

export default function App() {
	return (
		// Route modules are loaded from an effect after the location commits. React Router's
		// transition mode can retain a suspending editor forever and therefore prevent that
		// effect from running. The app already owns its non-blocking route progress UI, so a
		// synchronous location commit is both faster and deterministic here.
		<HistoryRouter history={applicationBrowserHistory} useTransitions={false}>
			<PreferredLocaleRoutes />
		</HistoryRouter>
	);
}
